import { EMPTY_ARRAY } from "@/lib/utils/stable-empty";
import React, { useState, useMemo, useCallback } from "react";
import { ResponsiveCommandSelect, SelectOption } from "../responsive-select";
import { BaseSelectProps, DynamicSelectProps } from "./types";

// Enhanced factory function that supports "Add New" functionality
export interface AddNewConfig {
  label: string;
  icon?: React.ReactNode;
  onAddNew: (onSuccess: (newItemId: string, newItem?: any) => void) => void;
}

// Factory function to create select components with dynamic options and optional "Add New" functionality
export function createDynamicSelectComponent<T>(
  optionMapper: (items: T[]) => SelectOption[],
  defaultPlaceholder: string,
  addNewConfig?: AddNewConfig
) {
  return function DynamicSelect({
    value,
    onValueChange,
    items = EMPTY_ARRAY,
    placeholder = defaultPlaceholder,
    disabled = false,
    error = false,
    required = false,
    isSearching,
    onSearchChange,
    searchTerm,
    highZIndex,
    fullWidth,
    ...otherProps
  }: DynamicSelectProps<T> & any) {
    // Memoize the regular options to prevent unnecessary re-renders
    const regularOptions = useMemo(() => optionMapper(items), [items]);

    // Memoize combined options with optional "Add New"
    const options = useMemo(() => {
      if (!addNewConfig) return regularOptions;

      const addNewOption: SelectOption = {
        value: "__ADD_NEW__",
        label: addNewConfig.label,
      };

      return [...regularOptions, addNewOption];
    }, [regularOptions, addNewConfig]);

    // Memoize the value change handler
    const handleValueChange = useCallback(
      (val: string, option?: SelectOption) => {
        if (val === "__ADD_NEW__" && addNewConfig) {
          addNewConfig.onAddNew((newItemId: string, newItem?: any) => {
            // Call the parent's onValueChange with the new item
            onValueChange(newItemId, {
              value: newItemId,
              label:
                newItem?.name || newItem?.company_name || newItem?.title || `Item ${newItemId}`,
            });
          });
        } else {
          onValueChange(val, option);
        }
      },
      [onValueChange, addNewConfig]
    );

    return (
      <ResponsiveCommandSelect
        value={value}
        onValueChange={handleValueChange}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        searchPlaceholder={`Search ${placeholder.toLowerCase()}...`}
        searchValue={searchTerm}
        onSearchChange={onSearchChange}
        isSearching={isSearching}
        title={otherProps.title}
        description={otherProps.description}
        {...otherProps}
      />
    );
  };
}

// Factory function for static select components with optional "Add New" functionality
export function createStaticSelectComponent(
  options: SelectOption[],
  defaultPlaceholder: string,
  addNewConfig?: AddNewConfig
) {
  return function StaticSelect({
    value,
    onValueChange,
    placeholder = defaultPlaceholder,
    disabled = false,
    error = false,
    required = false,
    highZIndex,
    fullWidth,
    ...otherProps
  }: BaseSelectProps & any) {
    // Memoize combined options with optional "Add New"
    const allOptions = useMemo(() => {
      if (!addNewConfig) return options;

      const addNewOption: SelectOption = {
        value: "__ADD_NEW__",
        label: addNewConfig.label,
      };

      return [...options, addNewOption];
    }, [addNewConfig]);

    // Memoize the value change handler
    const handleValueChange = useCallback(
      (val: string, option?: SelectOption) => {
        if (val === "__ADD_NEW__" && addNewConfig) {
          addNewConfig.onAddNew((newItemId: string, newItem?: any) => {
            // Call the parent's onValueChange with the new item
            onValueChange(newItemId, {
              value: newItemId,
              label: newItem?.name || newItem?.title || `Item ${newItemId}`,
            });
          });
        } else {
          onValueChange(val, option);
        }
      },
      [onValueChange, addNewConfig]
    );

    return (
      <ResponsiveCommandSelect
        value={value}
        onValueChange={handleValueChange}
        options={allOptions}
        placeholder={placeholder}
        disabled={disabled}
        searchPlaceholder={`Search ${placeholder.toLowerCase()}...`}
        title={otherProps.title}
        description={otherProps.description}
        {...otherProps}
      />
    );
  };
}

// Factory function for boolean select components (no "Add New" needed for boolean values)
export function createBooleanSelectComponent(
  trueValue: string,
  falseValue: string,
  trueLabel: string,
  falseLabel: string,
  defaultPlaceholder: string
) {
  const options: SelectOption[] = [
    { value: trueValue, label: trueLabel },
    { value: falseValue, label: falseLabel },
  ];

  return function BooleanSelect({
    value,
    onValueChange,
    placeholder = defaultPlaceholder,
    disabled = false,
    error = false,
    required = false,
  }: BaseSelectProps) {
    return (
      <ResponsiveCommandSelect
        value={value}
        onValueChange={(val, option) => onValueChange(val, option)}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
      />
    );
  };
}
